import {
  useCallback,
  useMemo,
  useReducer,
  useRef,
  type ChangeEvent,
  type FormEvent,
} from 'react';

import {
  GRID_N,
  centredStageRect,
  clampStageRect,
  clampsFor,
  stageRect,
  toImageRect,
  toStageRect,
} from '../lib/geometry';
import { TaintedCanvasError, renderSheets } from '../lib/export';
import { BrandMark } from './components/BrandMark';
import { Controls, HowItGoes, ThenExplainer } from './components/Controls';
import { EmptyState } from './components/EmptyState';
import { ExportView } from './components/ExportView';
import { Stage } from './components/Stage';
import { useBoardGestures } from './hooks/useBoardGestures';
import { useFit } from './hooks/useFit';
import { useImageLoader } from './hooks/useImageLoader';
import { useIsDesktop } from './hooks/useMediaQuery';
import { useMeasuredSize } from './hooks/useMeasuredSize';
import { initialState, reducer } from './state/editor';
import styles from './App.module.css';

export function App(): React.JSX.Element {
  const [state, dispatch] = useReducer(reducer, initialState);
  const isDesktop = useIsDesktop();

  const stageRef = useRef<HTMLDivElement>(null);
  const stage = useMeasuredSize(stageRef);
  const fit = useFit(stage, state.picture);

  const { loadFromUrl, loadFromFile } = useImageLoader({ dispatch, stage });

  const setBoard = useCallback(
    (board: Parameters<typeof toStageRect>[0]) => dispatch({ type: 'boardMoved', board }),
    [],
  );

  const handlers = useBoardGestures({
    stageRef,
    stage,
    fit,
    board: state.board,
    onBoard: setBoard,
  });

  /** Apply a change expressed in stage px: clamp, convert to image px, publish. */
  const applyStage = useCallback(
    (next: ReturnType<typeof stageRect>): void => {
      if (stage === null || fit === null) return;
      setBoard(toImageRect(clampStageRect(next, stage), fit));
    },
    [stage, fit, setBoard],
  );

  const boardOnStage =
    fit !== null && state.board !== null ? toStageRect(state.board, fit) : null;
  const clamps = stage !== null ? clampsFor(stage) : null;

  const dial = useMemo(() => {
    if (boardOnStage === null || fit === null || clamps === null) {
      return { percent: 0, fraction: 0, range: 1 };
    }
    const range = clamps.maxSize - clamps.minSize;
    return {
      // The reading in the middle: board size against the fitted picture — the "84%".
      percent: Math.round((boardOnStage.size / Math.min(fit.w, fit.h)) * 100),
      // The ring and knob: how far through the permitted size range we are.
      fraction: range === 0 ? 0 : (boardOnStage.size - clamps.minSize) / range,
      range,
    };
  }, [boardOnStage, fit, clamps]);

  const onSizeDelta = useCallback(
    (delta: number): void => {
      if (boardOnStage === null) return;
      const size = boardOnStage.size + delta;
      const grew = boardOnStage.size - size;
      // Resizing from the dial keeps the board centred where it is.
      applyStage(stageRect(boardOnStage.x + grew / 2, boardOnStage.y + grew / 2, size));
    },
    [boardOnStage, applyStage],
  );

  const onNudge = useCallback(
    (dx: number, dy: number): void => {
      if (boardOnStage === null) return;
      applyStage(stageRect(boardOnStage.x + dx, boardOnStage.y + dy, boardOnStage.size));
    },
    [boardOnStage, applyStage],
  );

  const onRecentre = useCallback((): void => {
    if (boardOnStage === null || stage === null) return;
    applyStage(centredStageRect(boardOnStage.size, stage));
  }, [boardOnStage, stage, applyStage]);

  const onGenerate = useCallback((): void => {
    const { picture, board } = state;
    if (picture === null || board === null) return;

    dispatch({ type: 'generateStarted' });

    // Yield once so the generating state paints before the synchronous encode blocks the
    // main thread (ADR-0009) — otherwise the click looks dead on a large picture.
    //
    // A macrotask, deliberately not requestAnimationFrame: rAF does not fire in a
    // backgrounded tab, so scheduling the export on it means a user who clicks and then
    // switches tabs never gets their sheets. A timeout still runs (throttled) and yields
    // long enough for the pending render to commit.
    setTimeout(() => {
      try {
        const result = renderSheets({
          image: picture.element,
          natural: picture.natural,
          rect: board,
          lineColor: state.line,
          labelScale: state.labelScale,
          n: GRID_N,
        });
        dispatch({ type: 'generated', result });
      } catch (cause) {
        dispatch({
          type: 'failed',
          message:
            cause instanceof TaintedCanvasError
              ? cause.message
              : 'Something went wrong making the sheets. Try again.',
        });
      }
    }, 0);
  }, [state]);

  const onFile = (event: ChangeEvent<HTMLInputElement>): void => {
    const file = event.target.files?.[0];
    if (file) loadFromFile(file);
    event.target.value = '';
  };

  const submitUrl = (event: FormEvent): void => {
    event.preventDefault();
    loadFromUrl(state.url);
  };

  const hasPicture = state.picture !== null;

  return (
    <div className={styles.app}>
      {isDesktop ? (
        <header className={styles.sourceBar}>
          <div className={styles.brand}>
            <BrandMark />
            <span className={styles.brandName}>Hide &amp; Seek Bingo</span>
          </div>

          <form className={styles.urlForm} onSubmit={submitUrl}>
            <input
              className="input"
              type="url"
              inputMode="url"
              placeholder="Paste a picture URL"
              value={state.url}
              onChange={(event) => dispatch({ type: 'urlChanged', url: event.target.value })}
              aria-label="Picture URL"
            />
            <button className="btn btn-primary" type="submit" disabled={state.loading}>
              {state.loading ? 'Loading…' : 'Load'}
            </button>
          </form>

          <label className={`btn btn-secondary ${styles.uploadLabel}`}>
            Upload a file
            <input type="file" accept="image/*" onChange={onFile} />
          </label>

          <button
            className={`btn btn-ghost ${styles.startOver}`}
            type="button"
            onClick={() => dispatch({ type: 'reset' })}
          >
            Start over
          </button>
        </header>
      ) : (
        <header className={styles.appHeader}>
          <BrandMark />
          <span className={styles.brandName}>Hide &amp; Seek Bingo</span>
          <button
            className={`btn btn-ghost ${styles.startOver}`}
            type="button"
            onClick={() => dispatch({ type: 'reset' })}
          >
            Start over
          </button>
        </header>
      )}

      <main className={styles.main}>
        <div className={styles.stageWell}>
          <Stage
            stageRef={stageRef}
            stage={stage}
            picture={state.picture}
            board={state.board}
            n={GRID_N}
            lineColor={state.line}
            labelScale={state.labelScale}
            handlers={handlers}
          >
            {!hasPicture && (
              <EmptyState
                showInputs={!isDesktop}
                url={state.url}
                loading={state.loading}
                error={state.error}
                onUrlChange={(url) => dispatch({ type: 'urlChanged', url })}
                onLoad={() => loadFromUrl(state.url)}
                onFile={loadFromFile}
              />
            )}
          </Stage>
        </div>

        <aside className={styles.panel}>
          {hasPicture ? (
            <Controls
              percent={dial.percent}
              fraction={dial.fraction}
              range={dial.range}
              labelScale={state.labelScale}
              lineColor={state.line}
              generating={state.generating}
              onSizeDelta={onSizeDelta}
              onNudge={onNudge}
              onRecentre={onRecentre}
              onLine={(line) => dispatch({ type: 'lineChanged', line })}
              onLabelScale={(labelScale) => dispatch({ type: 'labelScaleChanged', labelScale })}
              onGenerate={onGenerate}
              showHeaderHint={!isDesktop}
            />
          ) : isDesktop ? (
            <HowItGoes />
          ) : (
            <ThenExplainer />
          )}

          {hasPicture && state.error !== null && (
            <p className={styles.panelError} role="alert">
              {state.error}
            </p>
          )}
        </aside>
      </main>

      <ExportView
        result={state.result}
        tab={state.tab}
        isDesktop={isDesktop}
        onTab={(tab) => dispatch({ type: 'tabChanged', tab })}
        onBack={() => dispatch({ type: 'resultClosed' })}
      />
    </div>
  );
}
